import React from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import ProgressBar from './ProgressBar';
import styles from '../styles/stepCard.module.css';

export interface StepCardProps {
  stepNumber?: number;
  label?: string;
  title: string;
  description: string;
  status?: 'not-started' | 'in-progress' | 'completed';
  isLoggedIn: boolean;
  stepPath?: string;
  onStepClick?: () => void;
  onLoginClick?: () => void;
}

const StepCard: React.FC<StepCardProps> = ({
  stepNumber,
  label,
  title,
  description,
  status = 'not-started',
  isLoggedIn,
  stepPath,
  onStepClick,
  onLoginClick,
}) => {
  const { t } = useLocale();

  const getProgressValue = () => {
    switch (status) {
      case 'completed':
        return 100;
      case 'in-progress':
        return 50;
      default:
        return 0;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'completed':
        return t('working_steps.status.completed');
      case 'in-progress':
        return t('working_steps.status.in_progress');
      default:
        return t('working_steps.status.not_started');
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'in-progress':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const handleStepClick = () => {
    if (onStepClick) {
      onStepClick();
    }
  };

  const handleLoginClick = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const displayLabel =
    label ||
    (stepNumber !== undefined
      ? t('working_steps.card.step_label', { n: stepNumber })
      : t('working_steps.card.step_fallback'));

  const renderStepButton = () => {
    const buttonContent = (
      <div className={styles.stepButtonContent}>
        <span className={`${styles.stepButtonNumber} ${label ? styles.stepButtonCustomLabel : ''}`}>
          {displayLabel}
        </span>
        {isLoggedIn && (
          <span className={styles.stepButtonStatus} style={{ backgroundColor: getStatusColor() }}>
            {getStatusText()}
          </span>
        )}
      </div>
    );

    if (stepPath) {
      return (
        <Link to={stepPath} className={styles.stepButton}>
          {buttonContent}
        </Link>
      );
    }
    return (
      <button onClick={handleStepClick} className={styles.stepButton}>
        {buttonContent}
      </button>
    );
  };

  return (
    <div className={styles.stepCard}>
      {isLoggedIn ? (
        <div className={styles.stepActions}>{renderStepButton()}</div>
      ) : (
        <div className={styles.loginPrompt}>
          <p className={styles.loginText}>{t('working_steps.card.sign_in_prompt')}</p>
          <button onClick={handleLoginClick} className={styles.loginButton}>
            {t('working_steps.card.sign_in')}
          </button>
        </div>
      )}
      <div className={styles.stepTitle}>{title}</div>
      <div className={styles.stepDescription}>{description}</div>
    </div>
  );
};

export default StepCard;
